import { useEffect, useState, useRef } from "react";
import * as Icons from "lucide-react";
import { api } from "../lib/api";

/**
 * Small status pill in the Workspace header.
 *
 * Pings /api/_version and surfaces:
 *   - which environment we're talking to (Preview vs Production, derived
 *     from the current window's hostname)
 *   - the build version that's actually serving requests
 *   - the URL the backend would send to Magnific (resolved from forwarded
 *     headers — this is the URL that actually matters)
 *   - whether the baked-in PUBLIC_BACKEND_URL env still matches; mismatch
 *     just means the env is stale, not broken (we now use request-derived)
 *
 * Why this exists: for months Magnific jobs failed silently on Production
 * because the backend's hardcoded PUBLIC_BACKEND_URL pointed at Preview.
 * If that ever happens again, this pill will flag it instantly.
 */
export default function EnvHealthPill() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/_version");
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData({ error: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  if (loading) {
    return (
      <div
        className="hidden md:flex items-center gap-1.5 text-[10px] text-zinc-500 border border-white/10 rounded-md px-2 py-1"
        data-testid="env-health-pill-loading"
      >
        <Icons.Loader2 size={10} className="animate-spin" />
        env
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div
        className="hidden md:flex items-center gap-1.5 text-[10px] text-rose-300 border border-rose-500/30 rounded-md px-2 py-1"
        data-testid="env-health-pill-error"
        title="Could not reach /api/_version"
      >
        <Icons.AlertCircle size={10} />
        env: unreachable
      </div>
    );
  }

  // Derive environment label from the current window
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isPreview = host.includes("preview.emergentagent.com") || host.includes("localhost");
  const envLabel = isPreview ? "preview" : "production";

  // Healthy = backend resolved a base URL that matches its own request origin
  const healthy = !!data.healthy;
  // Stale env = the baked-in env doesn't match the request origin (informational
  // — not broken, since runtime resolution overrides this)
  const envStale = data.env_public_backend_url && !data.env_matches_request;

  const dotColor = healthy ? "bg-emerald-400" : "bg-rose-400";
  const ringColor = healthy ? "border-emerald-500/30" : "border-rose-500/30";

  return (
    <div className="relative hidden md:block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 text-[10px] text-zinc-300 hover:text-white border ${ringColor} rounded-md px-2 py-1 transition-colors`}
        data-testid="env-health-pill"
        title="Environment health — click for details"
      >
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor}`} />
        <span className="font-mono">{envLabel}</span>
        {envStale && (
          <span
            className="text-amber-300"
            title="PUBLIC_BACKEND_URL env var is stale — runtime resolution is correct, but you should update the env on next deploy"
          >
            ⚠
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[420px] bg-zinc-950 border border-white/10 rounded-lg shadow-2xl p-4 text-[11px] z-50"
          data-testid="env-health-panel"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-sm text-white">Environment health</div>
            <button
              onClick={() => setOpen(false)}
              className="text-zinc-500 hover:text-white"
              data-testid="env-health-panel-close"
            >
              <Icons.X size={14} />
            </button>
          </div>

          <Row label="Environment" value={envLabel} mono />
          <Row label="Build" value={data.version} mono />
          <div className="mt-3 mb-1 text-[10px] uppercase tracking-wider text-zinc-500">
            URL resolution
          </div>
          <Row
            label="Resolved (sent to Magnific)"
            value={data.resolved_base_url || "—"}
            mono
            ok={healthy}
            highlight
          />
          <Row label="Request origin" value={data.request_origin || "—"} mono />
          <Row
            label="PUBLIC_BACKEND_URL (env)"
            value={data.env_public_backend_url || "—"}
            mono
            warn={envStale}
          />
          <Row label="X-Forwarded-Host" value={data.forwarded_host || "—"} mono dim />
          <Row label="X-Forwarded-Proto" value={data.forwarded_proto || "—"} mono dim />

          <div className="mt-4 pt-3 border-t border-white/5 space-y-1.5">
            <Verdict
              ok={healthy}
              good="Magnific receives a URL that matches this environment — healthy."
              bad="Magnific would receive a URL that does NOT match this environment. Jobs will fail."
            />
            {envStale && (
              <div className="flex gap-2 text-amber-300/90">
                <Icons.AlertTriangle size={11} className="mt-0.5 shrink-0" />
                <div>
                  Baked-in <code className="font-mono text-amber-200">PUBLIC_BACKEND_URL</code> is
                  stale ({data.env_public_backend_url}). Runtime resolution is overriding it, so
                  things still work — but update the env on your next deploy to keep things tidy.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono, ok, warn, dim, highlight }) {
  let valueClass = "text-zinc-300";
  if (mono) valueClass += " font-mono";
  if (ok) valueClass = "text-emerald-300 font-mono";
  if (warn) valueClass = "text-amber-300 font-mono";
  if (dim) valueClass = "text-zinc-500 font-mono";
  return (
    <div
      className={`flex items-start justify-between gap-3 py-1 ${
        highlight ? "bg-white/[0.03] -mx-2 px-2 rounded" : ""
      }`}
    >
      <span className="text-zinc-500 shrink-0">{label}</span>
      <span className={`${valueClass} text-right break-all`}>{value}</span>
    </div>
  );
}

function Verdict({ ok, good, bad }) {
  return (
    <div className={`flex gap-2 ${ok ? "text-emerald-300" : "text-rose-300"}`}>
      {ok ? (
        <Icons.CheckCircle2 size={11} className="mt-0.5 shrink-0" />
      ) : (
        <Icons.XCircle size={11} className="mt-0.5 shrink-0" />
      )}
      <div>{ok ? good : bad}</div>
    </div>
  );
}
